import { INTERCOM_SURVEY_ID } from 'utils/constants/queryParam';

const PENDING_SURVEY_STORAGE_KEY = 'pending_intercom_survey';

const NUMERIC_ID_PATTERN = /^\d+$/;

/**
 * Captures an `intercom_survey_id` query param from a deep link (e.g. a Salesforce CSAT
 * email links to `https://app.busybusy.io?intercom_survey_id=<id>`) before the app renders,
 * stashing it in sessionStorage so it survives the login flow. The param is stripped from
 * the URL so it does not linger in the address bar or get re-triggered on refresh.
 *
 * A survey link carries no path, but one generated with the superseded `/ticket` path is
 * rewritten to `/` once the param is stripped, so the user is not stranded on the
 * unauthenticated "Invalid Ticket" screen.
 *
 * Must be called before the router mounts; the survey is fired later by the Intercom
 * middleware via `consumePendingIntercomSurveyId` once the user is identified.
 */
export function captureIntercomSurveyDeepLink() {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams(search);
  const surveyId = params.get(INTERCOM_SURVEY_ID);

  if (surveyId === null) {
    return;
  }

  params.delete(INTERCOM_SURVEY_ID);

  if (NUMERIC_ID_PATTERN.test(surveyId)) {
    try {
      window.sessionStorage.setItem(PENDING_SURVEY_STORAGE_KEY, surveyId);
    } catch {
      // sessionStorage unavailable; the link degrades to a normal login.
    }
  }

  const remainingSearch = params.toString();
  const isBareTicketPath =
    remainingSearch === '' && (pathname === '/ticket' || pathname.startsWith('/ticket/'));
  const newPath = isBareTicketPath ? '/' : pathname;

  window.history.replaceState(
    window.history.state,
    '',
    `${newPath}${remainingSearch ? `?${remainingSearch}` : ''}${hash}`
  );
}

/**
 * Returns a pending survey id captured by `captureIntercomSurveyDeepLink` and clears it,
 * or null if none is pending. Call only when Intercom is booted with the identified user,
 * since surveys with audience rules require identity.
 */
export function consumePendingIntercomSurveyId(): number | null {
  try {
    const value = window.sessionStorage.getItem(PENDING_SURVEY_STORAGE_KEY);

    if (value === null) {
      return null;
    }

    window.sessionStorage.removeItem(PENDING_SURVEY_STORAGE_KEY);

    return NUMERIC_ID_PATTERN.test(value) ? Number(value) : null;
  } catch {
    return null;
  }
}
